import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import { PatternFormat as NumberFormat } from 'react-number-format';

import OptionCard from "../../option-card";
import DatePicker from "../date-range/date-picker";

import CountrySelect from "../../select/country";
import StateSelect from "../../select/state";
import { deepEqual } from "../../../utils/object";

class Optional extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            configuration: { ...props.meta },
            data: { 
                ...(props.data ?? {}), 
                options: props.data?.options ?? props.meta?.default 
            },
            label: props.label,
            locked: Boolean(props.locked)
        }
    }

    getData = () => {
        return this.state.data;
    }

    componentDidUpdate = (oldProps) => {
        if (oldProps.data === this.props.data) return;
        const newData = {
            ...(this.props.data ?? {}),
            options: this.props.data?.options ?? this.props.meta?.default
        };
        if (deepEqual(this.state.data, newData)) return;
        // Just sync local state – no callback, no props.onChange here
        this.setState({
            data: newData
        });   
    }

    onHasChange = (e) => {
        const data = { ...this.state.data, options: e.target.value };
        
        // Clear fields for unselected options
        for (let i = 0; i < this.state.configuration.options.length; i++) {
            const option = this.state.configuration.options[i];
            if (data.options !== option.value.toString()) {
                for (let j = 0; j < option.controls?.length; j++) {
                    const control = option.controls[j];
                    data[control.key] = null;
                }
            }
        }

        this.setState({
            data
        }, () => {
            if (this.props.onChange) {
                this.props.onChange(); // Pass data directly
            }
        });
    }

    onChange = (e) => {
        const newData = {
            ...this.state.data,
            [e.target.name]: e.target.value
        };

        if (newData === this.state.data) return;
        
        this.setState({
            data: newData
        }, () => {
            if (this.props.onChange) {
                this.props.onChange(); // Pass newData directly
            }
        });
    }

    renderControl = (item) => {
        if (this.props.locked) {
            return (
                <Form.Control 
                    type="text" 
                    name={item.key} 
                    value={this.state.data[item.key] || ""} 
                    readOnly={true} 
                />
            );
        }

        switch (item.type) {
            case "FORMAT":
                return (
                    <NumberFormat 
                        format={item.format} 
                        mask={item.mask} 
                        className="form-control" 
                        value={this.state.data[item.key]} 
                        allowEmptyFormatting={item.allowEmptyFormatting} 
                        onChange={this.onChange} 
                        name={item.key} 
                    />
                );
            
            case "STATE":
                return (
                    <StateSelect 
                        value={this.state.data[item.key] || ""} 
                        onChange={(e) => this.onChange({ target: { name: item.key, value: e.value } })} 
                        placeholder="" 
                        name={item.key} 
                    />
                );
            
            case "COUNTRY":
                return (
                    <CountrySelect 
                        value={this.state.data[item.key] || ""} 
                        onChange={(e) => this.onChange({ target: { name: item.key, value: e.value } })} 
                        placeholder="" 
                        name={item.key} 
                    />
                );
            
            case "DATE":
                return (
                    <DatePicker 
                        className="form-control" 
                        selected={this.state.data[item.key]} 
                        onChange={(e) => this.onChange({ target: { name: item.key, value: e } })} 
                        placeholder="" 
                        name={item.key} 
                    />
                );
            
            case "TEXT":
                return (
                    <Form.Control
                        type="text"
                        name={item.key}
                        value={this.state.data[item.key] || ""}
                        onChange={(e) => this.onChange({ target: { name: item.key, value: e.target.value } })}
                    />
                );

            case "EMAIL":
                return (
                    <Form.Control
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        name={item.key}
                        value={this.state.data[item.key] || ""}
                        onChange={(e) => this.onChange({ target: { name: item.key, value: e.target.value } })}
                    />
                );
            
            default:
                return null;
        }
    }

    renderOptionControls = (optionIndex) => {
        const controls = this.state.configuration.options[optionIndex].controls;
        if (!controls || controls.length === 0) return null;

        return (
            <Col lg="6" className="mb-3">
                {controls.map((item, index) => (
                    <Row key={`${item.key}_${index}`}>
                        <Col>
                            <div className="g-3 mb-3 top-label">
                                {this.renderControl(item)}
                                <Form.Label>{item.label}</Form.Label>
                            </div>
                        </Col>
                    </Row>
                ))}
            </Col>
        );
    }

    render() {
        const option0Value = this.state.configuration.options[0].value?.toString();
        const option1Value = this.state.configuration.options[1].value?.toString();
        const currentValue = this.state.data?.options?.toString();

        return (
            <>
                {this.state.label && (
                    <Row className="g-3">
                        <Col lg="12">
                            <h2 className="small-title">{this.state.label}</h2>
                        </Col>
                    </Row>
                )}
                <Row className="g-3 mb-3">
                    <Col lg="3">
                        <OptionCard 
                            value={option0Value} 
                            checked={currentValue === option0Value} 
                            onChange={this.state.locked ? null : this.onHasChange} 
                            iconClassName={this.state.configuration.options[0].icon} 
                            iconClassColor={this.state.configuration.options[0].color} 
                            iconFontSize="48px" 
                            caption={this.state.configuration.options[0].caption} 
                        />
                    </Col>
                    {currentValue === option0Value && this.renderOptionControls(0)}
                    
                    <Col lg="3">
                        <OptionCard 
                            value={option1Value} 
                            checked={currentValue === option1Value} 
                            onChange={this.state.locked ? null : this.onHasChange} 
                            iconClassName={this.state.configuration.options[1].icon} 
                            iconClassColor={this.state.configuration.options[1].color} 
                            iconFontSize="48px" 
                            caption={this.state.configuration.options[1].caption} 
                        />
                    </Col>
                    {currentValue === option1Value && this.renderOptionControls(1)}
                </Row>
            </>
        )
    }
}

export default Optional;
